import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';

export interface ActivatableProps {
  role: 'button' | undefined;
  tabIndex: 0 | undefined;
  onClick: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onKeyDown: ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
  'aria-disabled': true | undefined;
}

/**
 * Makes a non-`<button>` element activatable — the single implementation of
 * "this span behaves like a button".
 *
 * Badges cannot be real `<button>`s: they have to stay valid inside flow
 * content (markdown `<p>`, notes, proposal copy), and invalid nesting there
 * breaks React hydration in the surrounding subtree. So they use
 * `role="button"` + `tabIndex` + Enter/Space instead — and that behaviour is
 * subtle enough (preventDefault on Space to stop page scroll, stopPropagation
 * so a badge inside a clickable row doesn't also trigger the row, no-op when
 * disabled) that every hand-rolled copy is a chance to get one part wrong.
 *
 * Consumed by `Badge` (via its `onClick` prop) and by `EntityBadge`, whose
 * variants attach these props to their own wrapper because they also carry
 * `aria-describedby`/`aria-expanded`/`data-entity-id` that must sit on the
 * focusable element. Both get identical semantics from here.
 *
 * Returns props that are all `undefined` when there is no `onClick`, so a
 * presentational element renders exactly as it would without this hook.
 */
export function useActivatable({
  onClick,
  isDisabled = false,
}: {
  onClick?: () => void;
  isDisabled?: boolean;
}): ActivatableProps {
  const isClickable = Boolean(onClick) && !isDisabled;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!isClickable || !onClick) {
        return;
      }
      // A badge often sits inside a clickable row or card; activating the badge
      // must not also activate its container.
      event.stopPropagation();
      onClick();
    },
    [isClickable, onClick]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!isClickable || !onClick) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        // Space would otherwise scroll the page.
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }
    },
    [isClickable, onClick]
  );

  return useMemo(
    () => ({
      // `role`/`aria-disabled` are announced whenever the element is *meant* to
      // be actionable, including while disabled — that is what tells a screen
      // reader "button, dimmed" rather than silently dropping the affordance.
      role: onClick ? ('button' as const) : undefined,
      tabIndex: isClickable ? (0 as const) : undefined,
      'aria-disabled': onClick && isDisabled ? (true as const) : undefined,
      onClick: onClick ? handleClick : undefined,
      onKeyDown: onClick ? handleKeyDown : undefined,
    }),
    [onClick, isClickable, isDisabled, handleClick, handleKeyDown]
  );
}
